import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Circle, Lock, Paperclip, Pencil, Plus, RotateCcw, Trash2, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { PageHeader } from '@/components/app/PageHeader';
import { EmptyState, PageSkeleton, Panel, PanelHeader } from '@/components/app/States';
import { Note } from '@/components/app/Badges';
import { Field, FormSheet, SheetSection, StatusPill, formatHistoryTime, inputClass, useDriftRefs } from '@/components/app/DriftUI';
import { AttachmentChip, Fact, PersonSelect } from '@/components/app/Maskinsaker';
import {
  DeviationBadges, DeviationDetailFields, DeviationKindMark, DeviationStatusPill, PrivacyHint, ProjectSelect, SeverityPicker, SeverityPill, SubprojectSelect, dateText, errText, formFromDeviation, isClarified, primaryBtn, textareaClass, today, validateDeviationForm,
} from '@/components/app/Avvikssaker';
import {
  ATTACHMENT_RULES, DEVIATION_KIND_LONG, addDeviationAction, addDeviationComment, attachmentProblem, closeDeviation, deleteAttachment, deleteDeviation, deleteDeviationAction, getDeviation,
  patchDeviation, patchDeviationAction, reopenDeviation, uploadDeviationAttachments,
} from '@/lib/driftApi';
import { cn } from '@/lib/utils';

/*
  Saksrom for ett manuelt avvik (RUH / kvalitet) – EKSEMPELDATA.
  Få, tydelige handlinger: start behandling → lukk med konklusjon → gjenåpne (historikken bevares).
  Venstre: beskrivelse, strakstiltak, korrigerende tiltak (ansvarlig, frist, utført) og kommentarer.
  Høyre: saksopplysninger (status, alvorlighet, ansvar, frist, prosjekt, kategorier – «Ikke avklart» vises ærlig), konklusjon, vedlegg og historikk.
*/

const DEFAULT_FROM = { label: 'avvikslisten', to: '/avvik?fane=hms' };
const sectionTitle = 'text-[13px] font-semibold uppercase tracking-[0.06em] text-[var(--hr-muted-2)]';

function SubHeading({ children, className }) {
  return <h3 className={cn(sectionTitle, className)}>{children}</h3>;
}

/** Kategoriverdi: avklarte verdier vanlig, «Ikke avklart» i amber – aldri skjult. */
function CategoryValue({ value, label, testId }) {
  if (!isClarified(value)) return <span className="font-medium text-[var(--hr-amber)]" data-testid={testId}>Ikke avklart</span>;
  return <span data-testid={testId}>{label}</span>;
}

/* ---------- Tiltak ---------- */

function ActionRow({ a, busy, onDone, onUndo, onRemove, closed }) {
  const [doneOpen, setDoneOpen] = useState(false);
  const [doneForm, setDoneForm] = useState({ date: today(), byId: a.responsibleId ?? '', note: '' });
  const refsState = useDriftRefs();
  const done = !!a.done;
  return (
    <li className={cn('px-5 py-3', done && 'bg-[var(--hr-surface-2)]/60')} data-testid={`action-${a.id}`}>
      <div className="flex items-start gap-3">
        <span className={cn('mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center', done ? 'text-[var(--hr-green)]' : a.overdue ? 'text-[var(--hr-amber)]' : 'text-[var(--hr-muted-2)]')} aria-hidden="true">
          {done ? <CheckCircle2 className="h-5 w-5" /> : <Circle className="h-5 w-5" />}
        </span>
        <div className="min-w-0 flex-1">
          <div className={cn('text-[14px] leading-5', done ? 'text-[var(--hr-muted)]' : 'font-medium text-[var(--hr-heading)]')} data-testid={`action-text-${a.id}`}>{a.text}</div>
          <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[13px] text-[var(--hr-muted)]">
            <span>{a.responsible ? a.responsible.name : <span className="text-[var(--hr-amber)]">Ansvarlig ikke satt</span>}</span>
            {a.dueDate && !done && <span className={cn(a.overdue && 'font-semibold text-[var(--hr-amber)]')}>Frist {dateText(a.dueDate)}{a.overdue ? ' · passert' : ''}</span>}
            {done && (
              <span className="text-[var(--hr-green)]" data-testid={`action-done-${a.id}`}>
                Utført {dateText(a.done.date)}{a.done.by ? ` · ${a.done.by.name}` : ''}{a.done.note ? ` – «${a.done.note}»` : ''}
              </span>
            )}
          </div>
          {doneOpen && (
            <div className="mt-2 grid gap-2 rounded-[10px] border border-[var(--hr-border)] bg-white p-3 sm:grid-cols-[minmax(0,140px)_minmax(0,1fr)]" data-testid={`action-done-form-${a.id}`}>
              <Field label="Utført dato" htmlFor={`ad-date-${a.id}`}>
                <Input id={`ad-date-${a.id}`} type="date" value={doneForm.date} max={today()} onChange={(e) => setDoneForm((f) => ({ ...f, date: e.target.value }))} className={cn(inputClass, 'h-10')} data-testid={`action-done-date-${a.id}`} />
              </Field>
              <Field label="Utført av" htmlFor={`ad-by-${a.id}`}>
                <PersonSelect id={`ad-by-${a.id}`} value={doneForm.byId} onChange={(v) => setDoneForm((f) => ({ ...f, byId: v }))} refs={refsState.refs} refsState={refsState} testId={`action-done-by-${a.id}`} />
              </Field>
              <Field label="Notat" htmlFor={`ad-note-${a.id}`} hint="Valgfritt – hva ble gjort?" className="sm:col-span-2">
                <Input id={`ad-note-${a.id}`} value={doneForm.note} maxLength={500} onChange={(e) => setDoneForm((f) => ({ ...f, note: e.target.value }))} className={cn(inputClass, 'h-10')} data-testid={`action-done-note-${a.id}`} />
              </Field>
              <div className="flex justify-end gap-2 sm:col-span-2">
                <Button variant="outline" size="sm" className="h-9 rounded-[8px] bg-white" onClick={() => setDoneOpen(false)} disabled={busy} data-testid={`action-done-cancel-${a.id}`}>Avbryt</Button>
                <Button size="sm" className={cn(primaryBtn, 'h-9 font-semibold')} onClick={async () => { const ok = await onDone(a, doneForm); if (ok) setDoneOpen(false); }} disabled={busy || !doneForm.date} data-testid={`action-done-save-${a.id}`}>
                  <CheckCircle2 className="mr-1.5 h-4 w-4" aria-hidden="true" /> Marker utført
                </Button>
              </div>
            </div>
          )}
        </div>
        {!closed && !doneOpen && (
          <div className="flex shrink-0 items-center gap-1">
            {done ? (
              <Button variant="ghost" size="sm" className="h-8 rounded-[8px] px-2 text-[13px] text-[var(--hr-muted)]" onClick={() => onUndo(a)} disabled={busy} data-testid={`action-undo-${a.id}`}>
                <Undo2 className="mr-1 h-3.5 w-3.5" aria-hidden="true" /> Angre
              </Button>
            ) : (
              <>
                <Button variant="outline" size="sm" className="h-8 rounded-[8px] bg-white px-2.5 text-[13px]" onClick={() => setDoneOpen(true)} disabled={busy} data-testid={`action-mark-done-${a.id}`}>
                  Marker utført
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 rounded-[8px] text-[var(--hr-muted)] hover:text-[var(--hr-red)]" onClick={() => onRemove(a)} disabled={busy} aria-label={`Fjern tiltaket ${a.text}`} data-testid={`action-remove-${a.id}`}>
                  <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

function AddActionForm({ onAdd, busy, refsState }) {
  const [form, setForm] = useState({ text: '', responsibleId: '', dueDate: '' });
  const [error, setError] = useState(null);
  const submit = async () => {
    if (form.text.trim().length < 3) {
      setError('Beskriv tiltaket (minst 3 tegn)');
      return;
    }
    setError(null);
    const ok = await onAdd({ text: form.text.trim(), responsibleId: form.responsibleId || null, dueDate: form.dueDate || null });
    if (ok) setForm({ text: '', responsibleId: '', dueDate: '' });
  };
  return (
    <div className="space-y-2 border-t border-[var(--hr-hairline)] px-5 py-4" data-testid="action-add-form">
      <Field label="Nytt korrigerende tiltak" htmlFor="na-text" error={error} hint={error ? undefined : 'Hva skal gjøres for at det ikke skjer igjen?'}>
        <Input id="na-text" value={form.text} maxLength={500} onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))} placeholder="F.eks. «Merke opp ryggesone og ventesone på kaia»" className={inputClass} data-testid="action-add-text" />
      </Field>
      <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,160px)_auto] sm:items-end">
        <Field label="Ansvarlig" htmlFor="na-by">
          <PersonSelect id="na-by" value={form.responsibleId} onChange={(v) => setForm((f) => ({ ...f, responsibleId: v }))} refs={refsState.refs} refsState={refsState} emptyLabel="Ikke satt" testId="action-add-by" />
        </Field>
        <Field label="Frist" htmlFor="na-due">
          <Input id="na-due" type="date" value={form.dueDate} onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))} className={inputClass} data-testid="action-add-due" />
        </Field>
        <Button variant="outline" className="h-11 rounded-[10px] bg-white lg:h-10" onClick={submit} disabled={busy} data-testid="action-add-save">
          <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" /> Legg til tiltak
        </Button>
      </div>
    </div>
  );
}

/* ---------- Historikk ---------- */

const HIST_KIND = { opprettet: 'Opprettet', status: 'Status endret', endret: 'Endret', lukket: 'Lukket', gjenapnet: 'Gjenåpnet', tiltak: 'Tiltak lagt til', tiltak_endret: 'Tiltak endret', tiltak_utfort: 'Tiltak utført', tiltak_angret: 'Tiltak satt tilbake', tiltak_fjernet: 'Tiltak fjernet', vedlegg: 'Vedlegg lagt til', vedlegg_fjernet: 'Vedlegg fjernet', kommentar: 'Kommentar' };

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const histValue = (v) => (v === null || v === undefined || v === '' ? 'tom' : typeof v === 'string' && ISO_DATE.test(v) ? v.split('-').reverse().join('.') : String(v));

function Timeline({ d }) {
  const items = useMemo(() => {
    const hist = (d.history ?? []).filter((h) => h.kind !== 'kommentar').map((h, i) => ({ key: `h-${i}`, at: h.at, kind: HIST_KIND[h.kind] ?? h.kind, by: h.by, text: h.note, reason: h.reason, changes: h.changes, tone: h.kind === 'lukket' || h.kind === 'tiltak_utfort' ? 'green' : h.kind === 'gjenapnet' ? 'amber' : h.kind === 'status' ? 'navy' : 'neutral' }));
    return hist.sort((a, b) => (a.at < b.at ? 1 : -1));
  }, [d]);
  if (!items.length) return <p className="text-[14px] text-[var(--hr-muted)]">Ingen hendelser i saken ennå.</p>;
  const dot = { green: 'bg-[var(--hr-green)]', amber: 'bg-[var(--hr-amber)]', navy: 'bg-[var(--hr-navy)]', neutral: 'bg-[var(--hr-muted-2)]' };
  return (
    <ol className="relative space-y-3 border-l border-[var(--hr-hairline)] pl-4" data-testid="dev-timeline">
      {items.map((it) => (
        <li key={it.key} className="relative" data-testid="dev-timeline-item">
          <span className={cn('absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full ring-2 ring-white', dot[it.tone])} aria-hidden="true" />
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[13px]">
            <span className="font-semibold text-[var(--hr-heading)]">{it.kind}</span>
            <span className="hr-tabular text-[var(--hr-muted)]">{formatHistoryTime(it.at)}</span>
            <span className="text-[var(--hr-muted-2)]">·</span>
            <span className="text-[var(--hr-muted)]">{it.by}</span>
          </div>
          {it.reason && <p className="mt-0.5 text-[13px] italic text-[var(--hr-text)]">«{it.reason}»</p>}
          {it.text && <p className="mt-0.5 text-[14px] leading-5 text-[var(--hr-text)]">{it.text}</p>}
          {it.changes?.length > 0 && (
            <dl className="mt-1 grid gap-0.5 text-[13px]">
              {it.changes.map((c, i) => (
                <div key={`${c.field}-${i}`} className="grid grid-cols-[minmax(0,110px)_minmax(0,1fr)] gap-2">
                  <dt className="truncate text-[var(--hr-muted)]">{c.label}</dt>
                  <dd className="min-w-0 break-words">
                    <span className="line-through decoration-[var(--hr-muted-2)]">{histValue(c.from)}</span>
                    <span className="mx-1.5 text-[var(--hr-muted-2)]">→</span>
                    <span className="font-medium text-[var(--hr-heading)]">{histValue(c.to)}</span>
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </li>
      ))}
    </ol>
  );
}

/* ---------- Rediger (samme felt som «Meld avvik») ---------- */

function EditSheet({ open, onOpenChange, d, onSave, busy }) {
  const refsState = useDriftRefs();
  const { refs } = refsState;
  const [form, setForm] = useState(() => formFromDeviation(d));
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState({});
  useEffect(() => {
    if (open) {
      setForm(formFromDeviation(d));
      setReason('');
      setErrors({});
    }
  }, [open, d]);
  const set = (k) => (v) => {
    setForm((f) => (k === 'projectId' && v !== f.projectId ? { ...f, projectId: v, subprojectId: '' } : { ...f, [k]: v }));
    setErrors((e) => (e[k] ? { ...e, [k]: undefined } : e));
  };
  const submit = () => {
    const e = validateDeviationForm(form);
    setErrors(e);
    if (Object.keys(e).length) return;
    const body = {};
    const cur = formFromDeviation(d);
    for (const k of ['title', 'date', 'time', 'projectId', 'subprojectId', 'description', 'severity', 'immediateAction', 'responsibleId', 'dueDate', 'cause', ...(d.kind === 'ruh' ? ['eventType', 'involved', 'workOperation'] : ['relatedTo'])]) {
      const v = typeof form[k] === 'string' ? form[k].trim() : form[k];
      if ((v ?? '') !== (cur[k] ?? '')) body[k] = v;
    }
    if (!Object.keys(body).length) {
      onOpenChange(false);
      return;
    }
    if (reason.trim()) body.reason = reason.trim();
    onSave(body);
  };
  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Rediger avvik"
      description="Endringer lagres som før → etter i historikken. Kategorier kan avklares her."
      testId="dev-edit-sheet"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" className="h-11 rounded-[10px] lg:h-10" onClick={() => onOpenChange(false)} disabled={busy} data-testid="dev-edit-cancel">Avbryt</Button>
          <Button className={cn(primaryBtn, 'h-11 px-5 text-[14px] font-semibold lg:h-10')} onClick={submit} disabled={busy} data-testid="dev-edit-save">{busy ? 'Lagrer …' : 'Lagre endringer'}</Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label="Kort tittel" htmlFor="de-title" required error={errors.title}>
          <Input id="de-title" value={form.title} maxLength={100} onChange={(e) => set('title')(e.target.value)} className={inputClass} data-testid="dev-edit-title" />
        </Field>
        <div className="grid grid-cols-[1fr_minmax(0,120px)] gap-3">
          <Field label="Dato" htmlFor="de-date" required error={errors.date}>
            <Input id="de-date" type="date" value={form.date} max={today()} onChange={(e) => set('date')(e.target.value)} className={inputClass} data-testid="dev-edit-date" />
          </Field>
          <Field label="Klokkeslett" htmlFor="de-time">
            <Input id="de-time" type="time" value={form.time} onChange={(e) => set('time')(e.target.value)} className={inputClass} data-testid="dev-edit-time" />
          </Field>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Prosjekt" htmlFor="de-project" required error={errors.projectId}>
            <ProjectSelect id="de-project" value={form.projectId} onChange={set('projectId')} refs={refs} testId="dev-edit-project" />
          </Field>
          <Field label="Underprosjekt" htmlFor="de-subproject">
            <SubprojectSelect id="de-subproject" value={form.subprojectId} onChange={set('subprojectId')} refs={refs} projectId={form.projectId} testId="dev-edit-subproject" />
          </Field>
        </div>
        <Field label="Alvorlighet" required>
          <SeverityPicker value={form.severity} onChange={set('severity')} testId="dev-edit-severity" />
        </Field>
        <Field label="Beskrivelse" htmlFor="de-desc">
          <Textarea id="de-desc" value={form.description} rows={4} maxLength={4000} onChange={(e) => set('description')(e.target.value)} className={textareaClass} data-testid="dev-edit-description" />
          <PrivacyHint className="mt-1.5" />
        </Field>
        <Field label="Strakstiltak" htmlFor="de-immediate">
          <Textarea id="de-immediate" value={form.immediateAction} rows={2} maxLength={2000} onChange={(e) => set('immediateAction')(e.target.value)} className={cn(textareaClass, 'min-h-[64px]')} data-testid="dev-edit-immediate" />
        </Field>
        <SheetSection title="Kategorier og ansvar" description="«Ikke avklart» er et ærlig valg til årsaken er undersøkt.">
          <DeviationDetailFields form={form} set={set} refs={refs} refsState={refsState} errors={errors} prefix="dev-edit" />
        </SheetSection>
        <Field label="Årsak til endringen" htmlFor="de-reason" hint="Valgfritt – vises i historikken">
          <Input id="de-reason" value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} className={inputClass} data-testid="dev-edit-reason" />
        </Field>
      </div>
    </FormSheet>
  );
}

/* ---------- Siden ---------- */

export default function AvvikSakPage() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const refsState = useDriftRefs();
  const { refs } = refsState;
  const [d, setD] = useState(null);
  const [status, setStatus] = useState('loading');
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(null); // 'close' | null
  const [editOpen, setEditOpen] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [closeForm, setCloseForm] = useState({ conclusion: '', date: today(), byId: '' });
  const [commentForm, setCommentForm] = useState({ text: '', byId: '' });
  const [errors, setErrors] = useState({});
  const fileRef = useRef(null);
  const from = location.state?.from ?? DEFAULT_FROM;

  const load = useCallback(async () => {
    try {
      const data = await getDeviation(id);
      setD(data);
      setStatus('ready');
    } catch (e) {
      setStatus(e?.status === 404 ? 'missing' : 'error');
    }
  }, [id]);

  useEffect(() => {
    setStatus('loading');
    setMode(null);
    load();
  }, [load]);

  useEffect(() => {
    if (d) document.title = `${d.title} · Avvik · Hardanger Rock`;
  }, [d]);

  /** Kjører en handling mot saken; true ved suksess. Feil vises som toast med backendens beskjed. */
  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      const next = await fn();
      setD(next);
      if (okMsg) toast.success(okMsg.title, { description: okMsg.description });
      return true;
    } catch (err) {
      toast.error('Kunne ikke oppdatere avviket', { description: errText(err) });
      return false;
    } finally {
      setBusy(false);
    }
  };

  if (status === 'loading' && !d) return <PageSkeleton />;
  if (status === 'missing') {
    return (
      <EmptyState
        title="Avviket finnes ikke"
        description="Saken er ukjent i eksempeldataene – den kan være slettet."
        action={
          <Button asChild size="sm">
            <Link to="/avvik?fane=hms" data-testid="dev-missing-back">Til avvikslisten</Link>
          </Button>
        }
      />
    );
  }
  if (status === 'error' && !d) {
    return (
      <Note tone="amber" data-testid="dev-load-error" role="alert">
        Kunne ikke hente avviket.{' '}
        <Button variant="link" className="h-auto px-1" onClick={load} data-testid="dev-load-retry">
          Prøv igjen
        </Button>
      </Note>
    );
  }

  const closed = !d.open;
  const openActions = (d.actions ?? []).filter((a) => !a.done);
  const unclear = [d.kind === 'ruh' ? ['eventType', d.eventType] : ['relatedTo', d.relatedTo], ['cause', d.cause]].filter(([, v]) => !isClarified(v)).length;

  const startBehandling = () => run(() => patchDeviation(d.id, { status: 'under_behandling' }), { title: 'Status: Under behandling' });
  const startClose = () => {
    setCloseForm({ conclusion: '', date: today(), byId: d.responsibleId ?? '' });
    setErrors({});
    setMode('close');
  };
  const submitClose = () => {
    const e = {};
    if (closeForm.conclusion.trim().length < 10) e.conclusion = 'Skriv en kort konklusjon (minst 10 tegn) – hva ble gjort og hvorfor avsluttes saken?';
    if (!closeForm.date) e.date = 'Velg dato';
    setErrors(e);
    if (Object.keys(e).length) return;
    run(() => closeDeviation(d.id, { conclusion: closeForm.conclusion.trim(), date: closeForm.date, byId: closeForm.byId || null }), { title: 'Avviket er lukket', description: openActions.length ? `${openActions.length} ${openActions.length === 1 ? 'tiltak gjensto' : 'tiltak gjensto'} ved lukking – vises fortsatt som åpne.` : 'Konklusjonen ligger i saken og historikken.' }).then((ok) => ok && setMode(null));
  };
  const submitReopen = () => run(() => reopenDeviation(d.id, { reason: reopenReason.trim() || null }), { title: 'Avviket er gjenåpnet', description: 'Tidligere konklusjon ligger igjen i historikken.' }).then((ok) => {
    if (ok) {
      setReopenOpen(false);
      setReopenReason('');
    }
  });
  const submitEdit = (body) => run(() => patchDeviation(d.id, body), { title: 'Avviket er oppdatert', description: 'Endringene ligger i historikken.' }).then((ok) => ok && setEditOpen(false));
  const addAction = (body) => run(() => addDeviationAction(d.id, body), { title: 'Tiltak lagt til' });
  const markDone = (a, f) => run(() => patchDeviationAction(d.id, a.id, { done: true, doneDate: f.date, doneById: f.byId || null, doneNote: f.note || null }), { title: 'Tiltak markert utført' });
  const undoDone = (a) => run(() => patchDeviationAction(d.id, a.id, { done: false }), { title: 'Tiltaket er satt tilbake til åpent' });
  const removeAction = (a) => run(() => deleteDeviationAction(d.id, a.id), { title: 'Tiltak fjernet' });
  const submitComment = async () => {
    if (commentForm.text.trim().length < 2) {
      setErrors((e) => ({ ...e, comment: 'Skriv en kommentar' }));
      return;
    }
    setErrors((e) => ({ ...e, comment: undefined }));
    const ok = await run(() => addDeviationComment(d.id, { text: commentForm.text.trim(), byId: commentForm.byId || null }), { title: 'Kommentar lagt til' });
    if (ok) setCommentForm({ text: '', byId: '' });
  };
  const removeAttachment = (att) => run(async () => {
    await deleteAttachment(att.id);
    return getDeviation(d.id);
  }, { title: 'Vedlegg fjernet' });
  const addFiles = async (list) => {
    const ok = [];
    for (const f of Array.from(list ?? [])) {
      const problem = attachmentProblem(f, (d.attachments?.length ?? 0) + ok.length);
      if (problem) toast.error('Vedlegget ble ikke lagt til', { description: problem });
      else ok.push(f);
    }
    if (fileRef.current) fileRef.current.value = '';
    if (!ok.length) return;
    await run(() => uploadDeviationAttachments(d.id, ok), { title: ok.length === 1 ? 'Vedlegg lagt til' : `${ok.length} vedlegg lagt til` });
  };
  const remove = async () => {
    setBusy(true);
    try {
      await deleteDeviation(d.id);
      toast.success('Avviket er slettet');
      navigate(from.to, { replace: true });
    } catch (err) {
      toast.error('Kunne ikke slette', { description: errText(err) });
    } finally {
      setBusy(false);
    }
  };

  const comments = [...(d.comments ?? [])].sort((a, b) => (a.at < b.at ? 1 : -1));

  return (
    <div className="space-y-5" data-testid="dev-case">
      <Link to={from.to} className="inline-flex items-center gap-1.5 rounded-sm text-[14px] font-medium text-[var(--hr-muted)] transition-colors duration-150 hover:text-[var(--hr-heading)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]" data-testid="dev-back-link">
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> Tilbake til {from.label}
      </Link>

      <PageHeader title={d.title} lead={`${DEVIATION_KIND_LONG[d.kind] ?? d.kindLabel} · ${dateText(d.date)}${d.time ? ` kl. ${d.time}` : ''} · ${d.project?.name ?? d.projectId}${d.subproject ? ` › ${d.subproject.name}` : ''}${d.reportedBy ? ` · meldt av ${d.reportedBy.name}` : ''}`}>
        {!d.seeded && (
          <Button variant="ghost" className="h-10 rounded-[10px] px-2 text-[14px] text-[var(--hr-muted)] hover:text-[var(--hr-red)]" onClick={() => setConfirmDelete(true)} disabled={busy} data-testid="dev-delete">
            <Trash2 className="mr-1.5 h-4 w-4" aria-hidden="true" /> Slett
          </Button>
        )}
        {!closed && (
          <Button variant="outline" className="h-10 rounded-[10px] bg-white px-3 text-[14px]" onClick={() => setEditOpen(true)} disabled={busy} data-testid="dev-edit">
            <Pencil className="mr-1.5 h-4 w-4 text-[var(--hr-muted)]" aria-hidden="true" /> Rediger
          </Button>
        )}
        {d.status === 'innmeldt' && (
          <Button className={cn(primaryBtn, 'h-10 px-4 text-[14px] font-semibold')} onClick={startBehandling} disabled={busy} data-testid="dev-action-start">
            Start behandling
          </Button>
        )}
        {!closed && (
          <Button variant={d.status === 'under_behandling' ? 'default' : 'outline'} className={cn('h-10 rounded-[10px] px-4 text-[14px]', d.status === 'under_behandling' ? `${primaryBtn} font-semibold` : 'bg-white')} onClick={startClose} disabled={busy || mode === 'close'} data-testid="dev-action-close">
            <Lock className="mr-1.5 h-4 w-4" aria-hidden="true" /> Lukk avviket
          </Button>
        )}
        {closed && (
          <Button className={cn(primaryBtn, 'h-10 px-4 text-[14px] font-semibold')} onClick={() => setReopenOpen(true)} disabled={busy} data-testid="dev-action-reopen">
            <RotateCcw className="mr-1.5 h-4 w-4" aria-hidden="true" /> Gjenåpne
          </Button>
        )}
      </PageHeader>

      <div className="flex flex-wrap items-center gap-1.5" data-testid="dev-badges">
        <DeviationKindMark kind={d.kind} long className="h-6 px-2 text-[12px]" />
        <DeviationBadges d={d} compact />
        {closed && d.closure?.openActionsAtClose > 0 && (
          <StatusPill tone="amber" data-testid="dev-open-actions-at-close">
            {d.closure.openActionsAtClose} tiltak gjensto ved lukking
          </StatusPill>
        )}
      </div>

      {closed && d.closure && (
        <section className="rounded-[12px] border border-[rgba(6,118,71,0.25)] bg-[var(--hr-green-bg)] px-5 py-4" data-testid="dev-closure">
          <h2 className="text-[14px] font-semibold text-[var(--hr-green)]">Lukket {dateText(d.closure.date)} · {d.closure.by?.name ?? 'ikke oppgitt'}</h2>
          <p className="mt-1 whitespace-pre-line text-[14px] leading-6 text-[var(--hr-text)]" data-testid="dev-conclusion">{d.closure.conclusion}</p>
        </section>
      )}

      {mode === 'close' && (
        <section className="rounded-[12px] border border-[var(--hr-border)] bg-[var(--hr-surface-2)] p-5" data-testid="dev-close-form">
          <h2 className="text-[16px] font-semibold text-[var(--hr-heading)]">Lukk avviket</h2>
          <p className="mt-0.5 text-[14px] leading-5 text-[var(--hr-muted)]">Lukking krever en kort, dokumentert konklusjon: hva ble gjort, og hvorfor avsluttes saken? Saken kan gjenåpnes senere – historikken bevares.</p>
          {openActions.length > 0 && (
            <Note tone="amber" className="mt-3" data-testid="dev-close-open-actions">
              {openActions.length} {openActions.length === 1 ? 'tiltak er ikke utført' : 'tiltak er ikke utført'}. Avviket kan lukkes likevel, men tiltakene vises fortsatt som åpne og tas med i rapporten.
            </Note>
          )}
          <div className="mt-4 space-y-3">
            <Field label="Konklusjon" htmlFor="cl-conclusion" required error={errors.conclusion}>
              <Textarea id="cl-conclusion" value={closeForm.conclusion} onChange={(e) => setCloseForm((f) => ({ ...f, conclusion: e.target.value }))} rows={4} maxLength={2000} placeholder="Årsak, hva som ble gjort, og hvorfor saken avsluttes …" className={cn(textareaClass, 'bg-white')} data-testid="dev-close-conclusion" autoFocus />
            </Field>
            <div className="grid gap-3 sm:grid-cols-[minmax(0,180px)_minmax(0,1fr)]">
              <Field label="Lukket dato" htmlFor="cl-date" required error={errors.date}>
                <Input id="cl-date" type="date" value={closeForm.date} min={d.date} max={today()} onChange={(e) => setCloseForm((f) => ({ ...f, date: e.target.value }))} className={inputClass} data-testid="dev-close-date" />
              </Field>
              <Field label="Lukket av" htmlFor="cl-by">
                <PersonSelect id="cl-by" value={closeForm.byId} onChange={(v) => setCloseForm((f) => ({ ...f, byId: v }))} refs={refs} refsState={refsState} testId="dev-close-by" />
              </Field>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" className="h-10 rounded-[10px] bg-white" onClick={() => setMode(null)} disabled={busy} data-testid="dev-close-cancel">Avbryt</Button>
              <Button className={cn(primaryBtn, 'h-10 font-semibold')} onClick={submitClose} disabled={busy} data-testid="dev-close-save">
                <Lock className="mr-1.5 h-4 w-4" aria-hidden="true" /> Lukk avviket
              </Button>
            </div>
          </div>
        </section>
      )}

      <div className="grid gap-5 lg:grid-cols-12">
        {/* Hovedspalte: hva skjedde, hva gjøres */}
        <div className="min-w-0 space-y-5 lg:col-span-7">
          <Panel>
            <div className="space-y-5 px-5 py-4">
              <section>
                <SubHeading>Beskrivelse</SubHeading>
                {d.description ? (
                  <p className="mt-1.5 whitespace-pre-line text-[15px] leading-6 text-[var(--hr-text)]" data-testid="dev-description">{d.description}</p>
                ) : (
                  <p className="mt-1.5 text-[14px] text-[var(--hr-muted)]" data-testid="dev-description-empty">
                    Ingen beskrivelse ennå.{' '}
                    {!closed && (
                      <button type="button" onClick={() => setEditOpen(true)} className="font-medium underline underline-offset-2 hover:text-[var(--hr-heading)]" data-testid="dev-description-add">
                        Legg til
                      </button>
                    )}
                  </p>
                )}
              </section>
              <section>
                <SubHeading>Strakstiltak</SubHeading>
                {d.immediateAction ? (
                  <p className="mt-1.5 whitespace-pre-line text-[14px] leading-6 text-[var(--hr-text)]" data-testid="dev-immediate">{d.immediateAction}</p>
                ) : (
                  <p className="mt-1.5 text-[14px] text-[var(--hr-muted)]" data-testid="dev-immediate-empty">Ikke registrert.</p>
                )}
              </section>
            </div>
          </Panel>

          <Panel data-testid="dev-actions-panel">
            <PanelHeader title={`Korrigerende tiltak${d.actions?.length ? ` · ${d.actionsDone}/${d.actions.length} utført` : ''}`} description="Konkrete tiltak med ansvarlig og frist. Marker utført med dato, hvem og et kort notat." />
            {d.actions?.length ? (
              <ul className="divide-y divide-[var(--hr-hairline)]" data-testid="dev-actions">
                {d.actions.map((a) => (
                  <ActionRow key={a.id} a={a} busy={busy} onDone={markDone} onUndo={undoDone} onRemove={removeAction} closed={closed} />
                ))}
              </ul>
            ) : (
              <p className="px-5 py-4 text-[14px] text-[var(--hr-muted)]" data-testid="dev-actions-empty">Ingen tiltak registrert ennå.</p>
            )}
            {!closed && <AddActionForm onAdd={addAction} busy={busy} refsState={refsState} />}
          </Panel>

          <Panel data-testid="dev-comments-panel">
            <PanelHeader title={`Kommentarer${comments.length ? ` · ${comments.length}` : ''}`} description="Observasjoner, avklaringer og status underveis. Fiktive medarbeidere." />
            {comments.length ? (
              <ul className="divide-y divide-[var(--hr-hairline)]" data-testid="dev-comments">
                {comments.map((c) => (
                  <li key={c.id} className="px-5 py-3" data-testid={`comment-${c.id}`}>
                    <div className="flex flex-wrap items-baseline gap-x-2 text-[13px]">
                      <span className="font-semibold text-[var(--hr-heading)]">{c.by?.name ?? 'Ikke oppgitt'}</span>
                      <span className="hr-tabular text-[var(--hr-muted)]">{formatHistoryTime(c.at)}</span>
                    </div>
                    <p className="mt-0.5 whitespace-pre-line text-[14px] leading-6 text-[var(--hr-text)]">{c.text}</p>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="px-5 py-4 text-[14px] text-[var(--hr-muted)]" data-testid="dev-comments-empty">Ingen kommentarer ennå.</p>
            )}
            <div className="space-y-2 border-t border-[var(--hr-hairline)] px-5 py-4" data-testid="dev-comment-form">
              <Textarea value={commentForm.text} onChange={(e) => setCommentForm((f) => ({ ...f, text: e.target.value }))} rows={2} maxLength={2000} placeholder="Skriv en kommentar …" aria-label="Kommentar" className={cn(textareaClass, 'min-h-[64px]')} data-testid="dev-comment-text" />
              {errors.comment && <p className="text-[13px] text-[var(--hr-red)]" role="alert">{errors.comment}</p>}
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <div className="min-w-0 flex-1">
                  <PersonSelect id="comment-by" value={commentForm.byId} onChange={(v) => setCommentForm((f) => ({ ...f, byId: v }))} refs={refs} emptyLabel="Hvem?" testId="dev-comment-by" />
                </div>
                <Button variant="outline" className="h-10 rounded-[10px] bg-white" onClick={submitComment} disabled={busy} data-testid="dev-comment-save">
                  Legg til kommentar
                </Button>
              </div>
            </div>
          </Panel>
        </div>

        {/* Sidespalte: saksopplysninger, vedlegg, historikk */}
        <div className="min-w-0 space-y-5 lg:col-span-5">
          <Panel data-testid="dev-facts-panel">
            <PanelHeader title="Saksopplysninger" actions={!closed && unclear > 0 ? <Button variant="outline" size="sm" className="h-8 rounded-[8px] bg-white text-[13px]" onClick={() => setEditOpen(true)} data-testid="dev-clarify">Avklar kategorier</Button> : null} />
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3.5 px-5 py-4" data-testid="dev-facts">
              <Fact label="Status" testId="dev-fact-status"><DeviationStatusPill status={d.status} /></Fact>
              <Fact label="Alvorlighet" testId="dev-fact-severity"><SeverityPill severity={d.severity} /></Fact>
              <Fact label="Ansvarlig" testId="dev-fact-responsible">{d.responsible?.name ?? <span className="text-[var(--hr-amber)]">Ikke tildelt</span>}</Fact>
              <Fact label="Frist" testId="dev-fact-due"><span className={cn(d.overdue && 'font-semibold text-[var(--hr-amber)]')}>{d.dueDate ? `${dateText(d.dueDate)}${d.overdue ? ' · passert' : ''}` : '–'}</span></Fact>
              <Fact label="Prosjekt" testId="dev-fact-project" className="col-span-2">
                {d.project ? (
                  <Link to={`/prosjekter/${d.project.id}`} state={{ from: { label: 'avviket', to: `${location.pathname}${location.search}` } }} className="font-medium text-[var(--hr-heading)] hover:underline" data-testid="dev-project-link">
                    {d.project.name}
                  </Link>
                ) : (
                  d.projectId
                )}
                {d.subproject && <span className="text-[var(--hr-muted)]"> › {d.subproject.name}</span>}
                {d.project?.location && <div className="text-[13px] text-[var(--hr-muted)]">{d.project.location}</div>}
              </Fact>
              <Fact label="Hendelsesdato" testId="dev-fact-date">{dateText(d.date)}{d.time ? ` kl. ${d.time}` : ''}</Fact>
              <Fact label="Meldt av" testId="dev-fact-reporter">{d.reportedBy?.name ?? '–'}</Fact>
              {d.kind === 'ruh' ? (
                <>
                  <Fact label="Type hendelse" testId="dev-fact-eventType"><CategoryValue value={d.eventType} label={d.eventTypeLabel} testId="dev-eventType" /></Fact>
                  <Fact label="Hendelsen omfattet" testId="dev-fact-involved"><CategoryValue value={d.involved} label={d.involvedLabel} testId="dev-involved" /></Fact>
                  <Fact label="Arbeidsoperasjon" testId="dev-fact-workOperation">{d.workOperationLabel ?? <span className="text-[var(--hr-muted)]">Ikke oppgitt</span>}</Fact>
                </>
              ) : (
                <Fact label="Relatert til" testId="dev-fact-relatedTo"><CategoryValue value={d.relatedTo} label={d.relatedToLabel} testId="dev-relatedTo" /></Fact>
              )}
              <Fact label="Årsak" testId="dev-fact-cause"><CategoryValue value={d.cause} label={d.causeLabel} testId="dev-cause" /></Fact>
            </dl>
            {unclear > 0 && (
              <p className="border-t border-[var(--hr-hairline)] px-5 py-2.5 text-[13px] leading-5 text-[var(--hr-muted)]" data-testid="dev-unclear-note">
                «Ikke avklart» betyr at kategorien ikke er undersøkt ennå – den settes når grunnlaget finnes, aldri gjettet.
              </p>
            )}
          </Panel>

          <Panel data-testid="dev-attachments-panel">
            <PanelHeader
              title={`Vedlegg${d.attachments?.length ? ` · ${d.attachments.length}` : ''}`}
              actions={
                <>
                  <input ref={fileRef} type="file" accept={ATTACHMENT_RULES.accept} multiple className="sr-only" onChange={(e) => addFiles(e.target.files)} data-testid="dev-attachments-input" />
                  <Button variant="ghost" size="sm" className="h-8 rounded-[8px] px-2 text-[13px]" onClick={() => fileRef.current?.click()} disabled={busy || (d.attachments?.length ?? 0) >= ATTACHMENT_RULES.maxPerEvent} data-testid="dev-attachments-add">
                    <Paperclip className="mr-1 h-3.5 w-3.5" aria-hidden="true" /> Legg til
                  </Button>
                </>
              }
            />
            <div className="px-5 py-4">
              {d.attachments?.length ? (
                <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3" data-testid="dev-attachments">
                  {d.attachments.map((att) => (
                    <AttachmentChip key={att.id} att={att} onDelete={closed ? undefined : removeAttachment} />
                  ))}
                </ul>
              ) : (
                <p className="text-[13px] text-[var(--hr-muted)]" data-testid="dev-attachments-empty">Ingen vedlegg. {ATTACHMENT_RULES.label}. Kun syntetiske eksempelfiler.</p>
              )}
            </div>
          </Panel>

          <Panel data-testid="dev-history-panel">
            <PanelHeader title="Historikk" description="Alle endringer, før → etter." />
            <div className="px-5 py-4">
              <Timeline d={d} />
              <p className="mt-4 text-[12px] leading-5 text-[var(--hr-muted-2)]">Historikken er en ærlig eksempelmarkering («Kontor (eksempeldata)» og fiktive medarbeidere) – ikke en autentisert revisjonslogg eller en påstand om HMS-etterlevelse.</p>
            </div>
          </Panel>
        </div>
      </div>

      <EditSheet open={editOpen} onOpenChange={setEditOpen} d={d} onSave={submitEdit} busy={busy} />

      <AlertDialog open={reopenOpen} onOpenChange={setReopenOpen}>
        <AlertDialogContent className="rounded-[16px]" data-testid="dev-reopen-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Gjenåpne avviket?</AlertDialogTitle>
            <AlertDialogDescription>Status settes til «Under behandling». Konklusjonen nullstilles, men blir liggende i historikken sammen med tiltak og kommentarer.</AlertDialogDescription>
          </AlertDialogHeader>
          <Field label="Årsak til gjenåpning" htmlFor="ro-reason" hint="Valgfritt – vises i historikken">
            <Input id="ro-reason" value={reopenReason} maxLength={300} onChange={(e) => setReopenReason(e.target.value)} className={inputClass} data-testid="dev-reopen-reason" />
          </Field>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-[10px]" data-testid="dev-reopen-cancel">Avbryt</AlertDialogCancel>
            <AlertDialogAction className={cn(primaryBtn, 'font-semibold')} onClick={(e) => { e.preventDefault(); submitReopen(); }} disabled={busy} data-testid="dev-reopen-confirm">
              <RotateCcw className="mr-1.5 h-4 w-4" aria-hidden="true" /> Gjenåpne
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent className="rounded-[16px]" data-testid="dev-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Slette avviket?</AlertDialogTitle>
            <AlertDialogDescription>Avviket, tiltakene, kommentarene og vedleggene fjernes fra eksempeldataene. Dette kan ikke angres.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-[10px]" data-testid="dev-delete-cancel">Avbryt</AlertDialogCancel>
            <AlertDialogAction className="rounded-[10px] bg-[var(--hr-red)] text-white hover:bg-[#9a1d14]" onClick={remove} disabled={busy} data-testid="dev-delete-confirm">
              Slett
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
